import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/client';
import {
  Globe, Loader2, RefreshCw, TrendingUp, ShoppingCart,
  Package, AlertTriangle, BarChart3, Eye
} from 'lucide-react';
import { useCan, Can } from '../../lib/rbac';

const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);
const fmtN = (n: number) => new Intl.NumberFormat('en-US').format(n);

interface SiteReport {
  items: { total: number; active: number; paused: number; under_review: number; closed: number };
  orders_30d: number;
  revenue_30d: number;
  aov: number;
  conversion_rate: number;
  top_categories: Array<{ category_id: string; category_name: string; order_count: number }>;
  trends: string[];
}

const SITE_FLAGS: Record<string, string> = {
  MLA: '🇦🇷', MLM: '🇲🇽', MLC: '🇨🇱', MCO: '🇨🇴', MLB: '🇧🇷', MLU: '🇺🇾', MPE: '🇵🇪',
};

const SITE_NAMES: Record<string, string> = {
  MLA: 'Argentina', MLM: 'México', MLC: 'Chile', MCO: 'Colombia', MLB: 'Brasil', MLU: 'Uruguay', MPE: 'Perú',
};

export default function MarketHealthPanel({ sellerId }: { sellerId: number }) {
  const canMoney = useCan()('global.view_money');
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [data, setData] = useState<Record<string, SiteReport> | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Read cached report from automation_config
      const res = await api.get<any>(`/scheduler/status?seller_id=${sellerId}`);
      if (res.success && res.data) {
        const mhProcess = (res.data as any).processes?.find((p: any) => p.feature_key === 'market_health');
        const lastReport = mhProcess?.settings?.last_report;
        if (lastReport?.sites) {
          setData(lastReport.sites);
          setGeneratedAt(lastReport.generated_at);
        }
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [sellerId]);

  useEffect(() => { loadData(); }, [loadData]);

  const runNow = async () => {
    setRunning(true);
    try {
      await api.post(`/scheduler/market_health/run?seller_id=${sellerId}`, {});
      setTimeout(loadData, 8000);
    } catch { /* ignore */ }
    setRunning(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  const sites = data ? Object.entries(data).sort((a, b) => b[1].orders_30d - a[1].orders_30d) : [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-emerald-500" />
          <h2 className="text-lg font-bold text-[var(--color-text)]">Market Health</h2>
          {generatedAt && (
            <span className="text-xs text-[var(--color-text-tertiary)]">
              Last: {new Date(generatedAt).toLocaleString()}
            </span>
          )}
        </div>
        <Can cap="ai.toggle_run">
          <button onClick={runNow} disabled={running}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-bg-tertiary)]">
            {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Run Analysis
          </button>
        </Can>
      </div>

      {/* Info banner */}
      <div className="p-3 rounded-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-xs text-[var(--color-text-tertiary)] leading-relaxed">
        <p className="font-semibold text-[var(--color-text-secondary)] mb-1">Que muestra este reporte?</p>
        <p>Una radiografia de cada mercado donde vendes. Para cada pais ves: cuantos items tenes activos, cuantas ventas generan, el ticket promedio, y que categorias mas venden. Tambien muestra las keywords que la gente busca en ese momento.</p>
        <p className="mt-1"><strong>Conversion Rate</strong> = Ordenes / Items activos. Si tenes 1000 items y 10 ventas = 1%. Mercados con muchos items y 0 ventas necesitan atencion (pricing, categorias, o falta de demanda).</p>
        <p className="mt-1">Para actualizar: click "Run Analysis" o activa el proceso en AI → Processes → Market Health (se actualiza automaticamente cada 12h).</p>
      </div>

      {sites.length === 0 ? (
        <div className="text-center py-12 text-[var(--color-text-tertiary)]">
          <Globe className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No market data available.</p>
          <p className="text-xs mt-1">Enable Market Health process and run the analysis.</p>
        </div>
      ) : (
        <>
          {/* Overview Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sites.map(([siteId, report]) => {
              const flag = SITE_FLAGS[siteId] || '🌎';
              const name = SITE_NAMES[siteId] || siteId;
              const items = report.items || { total: 0, active: 0, paused: 0, under_review: 0 };
              const reviewPct = items.total > 0 ? (items.under_review / items.total * 100) : 0;

              // Health score: simple heuristic
              let healthColor = 'green';
              let healthLabel = 'Healthy';
              if (report.orders_30d === 0 && items.active > 0) {
                healthColor = 'red'; healthLabel = 'Zero Sales';
              } else if (reviewPct > 10) {
                healthColor = 'yellow'; healthLabel = 'Review Issues';
              } else if (report.conversion_rate < 0.5 && items.active > 50) {
                healthColor = 'yellow'; healthLabel = 'Low Conversion';
              }

              return (
                <div key={siteId} className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4 space-y-3">
                  {/* Site header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{flag}</span>
                      <div>
                        <p className="font-bold text-[var(--color-text)]">{name}</p>
                        <p className="text-xs text-[var(--color-text-tertiary)]">{siteId}</p>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full bg-${healthColor}-500/10 text-${healthColor}-400`}>
                      {healthLabel}
                    </span>
                  </div>

                  {/* Items bar */}
                  <div>
                    <div className="flex justify-between text-xs text-[var(--color-text-tertiary)] mb-1">
                      <span>{items.active} active</span>
                      <span>{items.total} total</span>
                    </div>
                    <div className="h-2 bg-[var(--color-bg-tertiary)] rounded-full overflow-hidden flex">
                      <div className="bg-green-500 h-full" style={{ width: `${items.total > 0 ? items.active / items.total * 100 : 0}%` }} />
                      <div className="bg-yellow-500 h-full" style={{ width: `${items.total > 0 ? items.paused / items.total * 100 : 0}%` }} />
                      <div className="bg-red-500 h-full" style={{ width: `${items.total > 0 ? items.under_review / items.total * 100 : 0}%` }} />
                    </div>
                    <div className="flex gap-3 text-xs mt-1 text-[var(--color-text-tertiary)]">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> Active {items.active}</span>
                      {items.paused > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500" /> Paused {items.paused}</span>}
                      {items.under_review > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Review {items.under_review}</span>}
                    </div>
                  </div>

                  {/* KPIs */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-xs text-[var(--color-text-tertiary)]">Orders 30d</p>
                      <p className="text-sm font-bold text-[var(--color-text)]">{fmtN(report.orders_30d)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[var(--color-text-tertiary)]">Revenue</p>
                      <p className="text-sm font-bold text-green-400">{canMoney ? fmt(report.revenue_30d) : '\u2014'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[var(--color-text-tertiary)]">AOV</p>
                      <p className="text-sm font-bold text-[var(--color-text)]">{canMoney ? fmt(report.aov) : '\u2014'}</p>
                    </div>
                  </div>

                  {/* Conversion */}
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[var(--color-text-tertiary)]">Conversion Rate</span>
                    <span className={`font-medium ${report.conversion_rate >= 1 ? 'text-green-400' : report.conversion_rate >= 0.3 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {report.conversion_rate.toFixed(2)}%
                    </span>
                  </div>

                  {/* Top Categories */}
                  {report.top_categories?.length > 0 && (
                    <div>
                      <p className="text-xs text-[var(--color-text-tertiary)] mb-1">Top Categories</p>
                      <div className="space-y-1">
                        {report.top_categories.slice(0, 3).map((cat, i) => (
                          <div key={i} className="flex justify-between text-xs">
                            <span className="text-[var(--color-text-secondary)] truncate max-w-[70%]">{cat.category_name || cat.category_id}</span>
                            <span className="text-[var(--color-text-tertiary)]">{cat.order_count} orders</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Trends */}
                  {report.trends?.length > 0 && (
                    <div>
                      <p className="text-xs text-[var(--color-text-tertiary)] mb-1">Trending Keywords</p>
                      <div className="flex flex-wrap gap-1">
                        {report.trends.slice(0, 5).map((kw, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]">
                            {kw}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
